import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { useToast } from '~/common/components/defaultUIComponents';
import * as durationUtils from '~/common/utils/durationUtils';
import * as ReactRouter from 'react-router-dom';
import { FormikHelpers } from 'formik';
import * as yup from 'yup';
import React from 'react';

const required = 'validator.required';

export const CreateEditProjectSchema = yup
  .object({
    name: yup.string().required(required).min(3, 'validator.error.minLength3'),
    isOption: yup.boolean().required(required),
    hours: yup.number().required(required),
    minutes: yup.number().required(required),
    subProjectIds: yup.array(yup.string()).required(required),
    tags: yup.array(yup.string()).required(required),
  })
  .test({
    name: 'atLeastOneRequired',
    test: function (values) {
      const isValid = (['hours', 'minutes'] as const).some(
        (field) => (values[field] ?? 0) > 0,
      );

      if (isValid) return true;
      return this.createError({
        path: 'duration',
        message: 'project.create.durationError',
      });
    },
  });

export const useCreateEditProjectForm = () => {
  const navigate = ReactRouter.useNavigate();
  const params = ReactRouter.useParams<{ id: string }>();
  const toast = useToast({});
  const [{ data: project }] = ProjectApi.hooks.useProjectQuery(
    { id: params.id },
    {
      enabled: !!params.id,
    },
  );
  const { mutateAsync: createProject } =
    ProjectApi.hooks.useCreateProjectMutation();
  const { mutateAsync: editProject } =
    ProjectApi.hooks.useEditProjectMutation();

  const initialValues = React.useMemo(() => {
    return {
      name: project?.name ?? '',
      isOption: project?.is_option ?? false,
      subProjectIds: project?.option_ids?.map((id) => `${id}`) ?? [],
      ...durationUtils.asHMS(
        durationUtils.secondsToDuration(project?.calculated_project_time),
      ),
      tags: project?.tags ?? [],
    } as yup.InferType<typeof CreateEditProjectSchema> & {
      duration?: never;
    };
  }, [project]);

  const submit = React.useCallback(
    async (
      values: typeof initialValues,
      helpers: FormikHelpers<typeof initialValues>,
    ) => {
      const { hours, minutes } = values;
      try {
        project
          ? await editProject({
              id: project.id,
              name: values.name,
              is_option: values.isOption,
              calculated_project_time: durationUtils.normalizeToSeconds({
                hours,
                minutes,
              }),
              sub_projects: values.subProjectIds.filter(Boolean),
              tags: values.tags.filter(Boolean),
            })
          : await createProject({
              name: values.name,
              is_option: values.isOption,
              calculated_project_time: durationUtils.normalizeToSeconds({
                hours,
                minutes,
              }),
              tags: values.tags.filter(Boolean),
              sub_projects: values.subProjectIds.filter(Boolean),
            });

        helpers.resetForm();
        return navigate(-1);
      } catch (e) {
        return toast({
          status: 'error',
          duration: 5000,
          title: 'project.newProject.error',
        });
      }
    },
    [project],
  );

  return { initialValues, submit, project };
};
